/**
 * Management view — split-view settings for loyalty program management.
 *
 * Matches the /settings page design system: SplitView with gradient-icon
 * sidebar nav on the left, content area on the right.
 *
 * Plan 177, Epic 07 — Admin Management & Analytics
 * Plan 1833 — Wizard removed, replaced with free-form settings
 */

import {
  Gift,
  BarChart3,
  Settings,
  Coins,
  Trophy,
  Users,
  ShoppingBag,
  Ticket,
  Eye,
  Megaphone,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SplitView } from "@/components/layout/SplitView";
import { cn } from "@/lib/utils";
import type { RewardsConfigResponse } from "../api/rewardsAdminHooks";
import { OverviewTab } from "./OverviewTab";
import { ProgramSettingsTab } from "./ProgramSettingsTab";
import { EarnRulesTab } from "./EarnRulesTab";
import { ActivitiesTab } from "./ActivitiesTab";
import { CatalogTab } from "./CatalogTab";
import { MembersTab } from "./MembersTab";
import { ClaimedRewardsTab } from "./ClaimedRewardsTab";
import { MonthlyCouponsTab } from "./MonthlyCouponsTab";
import { LoyaltyDisplaySettingsTab } from "./LoyaltyDisplaySettingsTab";
import { CampaignsTab } from "./CampaignsTab";

interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  color: string;
}

const navItems: NavItem[] = [
  { id: "overview", label: "ภาพรวม", icon: BarChart3, color: "bg-gradient-to-br from-sky-500 to-blue-600" },
  { id: "program-settings", label: "ตั้งค่าโปรแกรม", icon: Settings, color: "bg-gradient-to-br from-gray-500 to-gray-600" },
  { id: "earn-rules", label: "วิธีสะสมแต้ม", icon: Coins, color: "bg-gradient-to-br from-amber-500 to-orange-500" },
  { id: "activities", label: "กิจกรรม", icon: Sparkles, color: "bg-gradient-to-br from-violet-500 to-purple-600" },
  { id: "catalog", label: "รางวัล", icon: Trophy, color: "bg-gradient-to-br from-yellow-500 to-amber-500" },
  { id: "members", label: "สมาชิก", icon: Users, color: "bg-gradient-to-br from-green-500 to-green-600" },
  { id: "claimed", label: "การแลกรางวัล", icon: ShoppingBag, color: "bg-gradient-to-br from-purple-500 to-purple-600" },
  { id: "monthly-coupons", label: "คูปองรายเดือน", icon: Ticket, color: "bg-gradient-to-br from-pink-500 to-rose-500" },
  { id: "campaigns", label: "แคมเปญ", icon: Megaphone, color: "bg-gradient-to-br from-orange-500 to-red-500" },
  { id: "display-settings", label: "ตั้งค่าการแสดงผล", icon: Eye, color: "bg-gradient-to-br from-teal-500 to-emerald-600" },
];

const sectionTitles: Record<string, { title: string; subtitle: string }> = {
  overview: { title: "ภาพรวม", subtitle: "สรุปข้อมูลโปรแกรมสะสมแต้ม" },
  "program-settings": { title: "ตั้งค่าโปรแกรม", subtitle: "ชื่อโปรแกรม อัตราสะสมแต้ม และวันหมดอายุ" },
  "earn-rules": { title: "วิธีสะสมแต้ม", subtitle: "กำหนดเงื่อนไขการสะสมแต้ม" },
  activities: { title: "กิจกรรม", subtitle: "สร้างกิจกรรมให้ลูกค้าสะสมแต้มจากการทำสิ่งต่างๆ" },
  catalog: { title: "รางวัล", subtitle: "จัดการรางวัลสำหรับแลกแต้ม" },
  members: { title: "สมาชิก", subtitle: "รายชื่อสมาชิกและแต้มสะสม" },
  claimed: { title: "การแลกรางวัล", subtitle: "ประวัติการแลกรางวัลของลูกค้า" },
  "monthly-coupons": { title: "คูปองรายเดือน", subtitle: "ตั้งค่าคูปองแจกรายเดือน" },
  campaigns: { title: "แคมเปญ", subtitle: "สร้างและจัดการแคมเปญรางวัลสำหรับกลุ่มเป้าหมาย" },
  "display-settings": { title: "ตั้งค่าการแสดงผล", subtitle: "เปิด-ปิดการแสดงแต้มในหน้าต่าง ๆ" },
};

interface ManagementViewProps {
  config: RewardsConfigResponse;
}

const validTabIds = new Set(navItems.map((item) => item.id));

export function ManagementView({ config }: ManagementViewProps) {
  const defaultSection = config.configured ? "overview" : "program-settings";
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeSection =
    tabParam && validTabIds.has(tabParam) ? tabParam : defaultSection;

  const setActiveSection = (id: string) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  const section = sectionTitles[activeSection];

  const renderContent = () => {
    switch (activeSection) {
      case "overview":
        return <OverviewTab />;
      case "program-settings":
        return <ProgramSettingsTab />;
      case "earn-rules":
        return <EarnRulesTab config={config} />;
      case "activities":
        return <ActivitiesTab />;
      case "catalog":
        return <CatalogTab />;
      case "members":
        return <MembersTab />;
      case "claimed":
        return <ClaimedRewardsTab />;
      case "monthly-coupons":
        return <MonthlyCouponsTab />;
      case "campaigns":
        return <CampaignsTab />;
      case "display-settings":
        return <LoyaltyDisplaySettingsTab />;
      default:
        return <OverviewTab />;
    }
  };

  const leftContent = (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
            <Gift className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">
              {config.loyalty_program_name || "รางวัลลูกค้า"}
            </h2>
            <p className="text-xs text-muted-foreground">
              {config.configured
                ? "โปรแกรมกำลังทำงาน"
                : "เริ่มต้นตั้งค่า"}
            </p>
          </div>
        </div>
      </div>

      {/* Nav items */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-1">
        {navItems.map((item) => {
          const isActive = activeSection === item.id;
          const Icon = item.icon;
          return (
            <Button
              key={item.id}
              variant="ghost"
              className={cn(
                "w-full justify-start gap-3 px-2 py-3 rounded transition-all duration-300",
                "hover:scale-[1.02] active:scale-[0.98]",
                isActive
                  ? "bg-white/60 dark:bg-gray-800/60 text-gray-900 dark:text-gray-100 shadow-apple"
                  : "text-gray-700 dark:text-gray-300 hover:bg-white/40 dark:hover:bg-gray-800/30",
              )}
              onClick={() => setActiveSection(item.id)}
            >
              <div
                className={cn(
                  "w-6 h-6 rounded-sm flex items-center justify-center shrink-0 transition-all duration-300",
                  item.color,
                  isActive ? "scale-105 shadow-md" : "scale-100",
                )}
              >
                <Icon className="w-3 h-3 text-white" />
              </div>
              {item.label}
            </Button>
          );
        })}
      </div>
    </div>
  );

  const rightContent = (
    <div className="p-6">
      <div className="space-y-6">
        {/* Section header */}
        {section && (
          <div>
            <h2 className="text-2xl font-semibold mb-2">{section.title}</h2>
            <p className="text-muted-foreground">{section.subtitle}</p>
          </div>
        )}
        {renderContent()}
      </div>
    </div>
  );

  return (
    <SplitView left={leftContent} right={rightContent} leftClassName="!w-64" />
  );
}
